import { pickLocalized } from './localized'
import { LOCATIONS } from './locations'
import { getSkillLevel } from './skills'
import type { ItemStack, LanguageCode, LocationId, Needs, Survivor } from './types'

/**
 * Central knobs for needs-based loot penalties/bonus. Tune here only — gameplay
 * (`needsLootMultiplier`, `rollLoot`) and the assign-sheet estimate both read this.
 *
 * - **criticalLootMultPerNeed**: Applied once per need whose rounded value is
 *   strictly below `criticalBelow`. Use values well below 1 for a heavy hit (they multiply).
 * - **estimateRollRatioCapWhenCritical / Low**: UI-only caps on how much extra
 *   looting rolls can inflate the shown “±% loot” when needs are bad, so high skill
 *   does not mask starvation/thirst/energy warnings.
 */
export type NeedsLootTuning = {
  /** All three rounded needs must be strictly greater than this for the well-rested bonus */
  wellRestedMin: number
  wellRestedLootMult: number
  /** Rounded need strictly below this uses the critical multiplier */
  criticalBelow: number
  criticalLootMultPerNeed: number
  /** Inclusive upper bound of the “low” band (still penalized, milder than critical) */
  lowBandMax: number
  lowLootMultPerNeed: number
  /** UI: max (avgBase+bonus)/avgBase roll ratio when any need is critical */
  estimateRollRatioCapWhenCritical: number
  /** UI: max roll ratio when any need is low but none critical */
  estimateRollRatioCapWhenLow: number
}

export const NEEDS_LOOT_TUNING: NeedsLootTuning = {
  wellRestedMin: 90,
  wellRestedLootMult: 1.15,
  criticalBelow: 20,
  criticalLootMultPerNeed: 0.5,
  lowBandMax: 50,
  lowLootMultPerNeed: 0.82,
  estimateRollRatioCapWhenCritical: 1.2,
  estimateRollRatioCapWhenLow: 1.45,
}

/** Neutral reference for yield comparison (no bonus band, no penalty band) */
const REF_NEEDS: Needs = { hunger: 75, thirst: 75, energy: 75 }

/** Baseline bonus rolls vs “average” survivor: looting 1 → floor(1/2) */
const REF_LOOT_BONUS_ROLLS = 0

const randInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min

function perNeedFactor(rounded: number): number {
  const { criticalBelow, lowBandMax, criticalLootMultPerNeed, lowLootMultPerNeed } =
    NEEDS_LOOT_TUNING
  if (rounded < criticalBelow) return criticalLootMultPerNeed
  if (rounded <= lowBandMax) return lowLootMultPerNeed
  return 1
}

/**
 * Multiplies rolled loot quantities. All three needs above `wellRestedMin` apply a single bonus;
 * otherwise each need contributes a stacking penalty factor when low.
 */
export function needsLootMultiplier(needs: Needs): number {
  const { wellRestedMin, wellRestedLootMult } = NEEDS_LOOT_TUNING
  const h = Math.round(needs.hunger)
  const t = Math.round(needs.thirst)
  const m = Math.round(needs.energy)
  if (h > wellRestedMin && t > wellRestedMin && m > wellRestedMin) {
    return wellRestedLootMult
  }
  return perNeedFactor(h) * perNeedFactor(t) * perNeedFactor(m)
}

export function lootBonusRollsFor(survivor: Survivor): number {
  const looting = getSkillLevel(survivor, 'looting')
  return Math.floor(looting / 2)
}

/** Rounded needs snapshot for UI tiers (same bands as loot penalties). */
export function survivorNeedsLootBands(survivor: Survivor): {
  hasCritical: boolean
  hasLow: boolean
} {
  const { criticalBelow, lowBandMax } = NEEDS_LOOT_TUNING
  const values = [
    Math.round(survivor.needs.hunger),
    Math.round(survivor.needs.thirst),
    Math.round(survivor.needs.energy),
  ]
  const hasCritical = values.some((v) => v < criticalBelow)
  const hasLow = values.some(
    (v) => v >= criticalBelow && v <= lowBandMax,
  )
  return { hasCritical, hasLow }
}

/**
 * Estimated loot tendency vs baseline (ref needs 75/75/75, ref bonus rolls 0),
 * for UI only. When needs are critical/low, roll-ratio is capped so looting
 * cannot wash out the penalty in the displayed percentage.
 */
export function estimateLootYieldVsBaselinePct(
  survivor: Survivor,
  locationRolls: { min: number; max: number },
): number {
  const {
    estimateRollRatioCapWhenCritical,
    estimateRollRatioCapWhenLow,
  } = NEEDS_LOOT_TUNING
  const avgBase = (locationRolls.min + locationRolls.max) / 2
  const bonusActual = lootBonusRollsFor(survivor)
  const denom = avgBase + REF_LOOT_BONUS_ROLLS
  let rollRatio = denom > 0 ? (avgBase + bonusActual) / denom : 1

  const { hasCritical, hasLow } = survivorNeedsLootBands(survivor)
  if (hasCritical) {
    rollRatio = Math.min(rollRatio, estimateRollRatioCapWhenCritical)
  } else if (hasLow) {
    rollRatio = Math.min(rollRatio, estimateRollRatioCapWhenLow)
  }

  const refNeedsMult = needsLootMultiplier(REF_NEEDS)
  const needsRatio =
    refNeedsMult > 0 ? needsLootMultiplier(survivor.needs) / refNeedsMult : 1
  const ratio = needsRatio * rollRatio
  return Math.round((ratio - 1) * 100)
}

/**
 * Rolls expedition loot as a list of ItemStacks. Foraging skill grants
 * bonus rolls (+1 per 2 levels). Stacks of the same item are merged.
 * Low needs reduce quantities; all needs above `wellRestedMin` increases them.
 * An optional vehicle multiplier is applied on top of the needs multiplier.
 */
export function rollLoot(
  locationId: LocationId,
  survivor?: Survivor,
  vehicleLootMultiplier = 1,
): ItemStack[] {
  const loc = LOCATIONS[locationId]
  const totalWeight = loc.loot.reduce((s, l) => s + l.weight, 0)
  const baseRolls = randInt(loc.rolls.min, loc.rolls.max)
  const bonusRolls = survivor ? lootBonusRollsFor(survivor) : 0
  const rolls = baseRolls + bonusRolls

  const counts = new Map<string, number>()

  for (let i = 0; i < rolls; i++) {
    let r = Math.random() * totalWeight
    for (const entry of loc.loot) {
      r -= entry.weight
      if (r <= 0) {
        const qty = randInt(entry.min, entry.max)
        counts.set(entry.itemId, (counts.get(entry.itemId) ?? 0) + qty)
        break
      }
    }
  }

  const stacks = Array.from(counts.entries()).map(([defId, quantity]) => ({
    defId,
    quantity,
  }))

  if (!survivor) return stacks

  const nm = needsLootMultiplier(survivor.needs) * vehicleLootMultiplier
  return stacks
    .map(({ defId, quantity }) => ({
      defId,
      quantity: Math.max(0, Math.round(quantity * nm)),
    }))
    .filter((s) => s.quantity > 0)
}

export function expeditionCostFor(
  locationId: LocationId,
  survivor: Survivor,
) {
  const base = LOCATIONS[locationId].cost
  const stealth = getSkillLevel(survivor, 'stealth')
  const endurance = getSkillLevel(survivor, 'endurance')
  const survival = getSkillLevel(survivor, 'survival')
  return {
    hunger: Math.max(0, base.hunger - endurance * 3),
    thirst: Math.max(0, base.thirst - endurance * 2),
    energy: Math.max(0, base.energy - stealth * 3 - survival * 1),
  }
}

/**
 * Picks 3 diary lines for an expedition deterministically-ish. Uses
 * Math.random to seed at expedition-start; the order is preserved on
 * the expedition record so the UI cycles through them stably.
 */
export function pickDiaryLines(
  locationId: LocationId,
  lang: LanguageCode,
): string[] {
  const pool = LOCATIONS[locationId].diary
  if (pool.length === 0) return ['', '', '']
  const indices = new Set<number>()
  while (indices.size < Math.min(3, pool.length)) {
    indices.add(Math.floor(Math.random() * pool.length))
  }
  return Array.from(indices).map((i) => pickLocalized(pool[i], lang))
}
